import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Chip,
} from '@mui/material';
import { ContentCopy } from '@mui/icons-material';
import { useSites } from '../../hooks/useFleet';

interface DuplicateZoneDialogProps {
  open: boolean;
  onClose: () => void;
  zoneId: string;
  zoneName: string;
  currentSiteId?: string;
  onDuplicate: (params: { name?: string; targetSiteId?: string }) => Promise<void>;
}

export const DuplicateZoneDialog: React.FC<DuplicateZoneDialogProps> = ({
  open,
  onClose,
  zoneId,
  zoneName,
  currentSiteId,
  onDuplicate,
}) => {
  const [name, setName] = useState('');
  const [targetSiteId, setTargetSiteId] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ schedules_copied?: number; entries_copied?: number } | null>(null);

  const { data: sitesData, isLoading: sitesLoading } = useSites({ size: 100 });
  const sites = sitesData?.data || [];

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setName(`${zoneName} (Copy)`);
      setTargetSiteId(currentSiteId || '');
      setError(null);
      setResult(null);
    }
  }, [open, zoneName, currentSiteId]);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError(null);
    try {
      await onDuplicate({
        name: name || undefined,
        targetSiteId: targetSiteId || undefined,
      });
      onClose();
    } catch (err) {
      setError((err as Error)?.message || 'Failed to duplicate');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <ContentCopy />
        Duplicate Zone
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Create a copy of "{zoneName}" with all its schedules and time entries in the selected site.
          </Typography>

          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {result && (
            <Alert severity="success">
              Duplicated successfully: {result.schedules_copied} schedules, {result.entries_copied} entries copied
            </Alert>
          )}

          <TextField
            label="New Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            helperText="Leave empty to use default name with (Copy) suffix"
          />

          <FormControl fullWidth>
            <InputLabel>Target Site</InputLabel>
            <Select
              value={targetSiteId}
              label="Target Site"
              onChange={(e) => setTargetSiteId(e.target.value)}
              disabled={sitesLoading}
            >
              {sitesLoading ? (
                <MenuItem disabled>
                  <CircularProgress size={16} sx={{ mr: 1 }} />
                  Loading sites...
                </MenuItem>
              ) : (
                sites.map((site) => (
                  <MenuItem key={site.id} value={site.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                      <span>{site.name}</span>
                      {site.id === currentSiteId && (
                        <Chip label="current" size="small" variant="outlined" />
                      )}
                    </Box>
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>

          <Typography variant="caption" color="text.secondary">
            This will copy the zone configuration, all schedules, and all time entries (playlists/sequences assignments).
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isSubmitting || sitesLoading}
          startIcon={isSubmitting ? <CircularProgress size={16} /> : <ContentCopy />}
        >
          {isSubmitting ? 'Duplicating...' : 'Duplicate'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
